import {
  type GetHybridCloudEnvironmentResponseValid,
  HybridCloudService,
} from '@qdrant/qdrant-cloud-public-api/hybrid/v1';
import {
  createHybridCloudEnvironment,
  deleteHybridCloudEnvironment,
  generateBootstrapCommands,
  getHybridCloudEnvironment,
  listHybridCloudEnvironments,
  updateHybridCloudEnvironment,
} from '@qdrant/qdrant-cloud-public-api/hybrid/v1/connect-query';
import {
  getCloudProviderRegion,
  listCloudProviderRegions,
} from '@qdrant/qdrant-cloud-public-api/platform/v1/connect-query';
import { transport } from '@/api/transport';
import { CLOUD_PROVIDER_MAP } from '@/components/Clusters/constants';
import {
  queryOptions,
  createConnectQueryKey,
  type MutationFactory,
  useMutation,
  createServiceQueries,
} from '@/connect-query';

export const HybridCloudServices = createServiceQueries(HybridCloudService, {
  getHybridCloudEnvironmentQuery: (params) => {
    const queryOpts = queryOptions(getHybridCloudEnvironment, params, { transport });

    return {
      ...queryOpts,
      staleTime: 1000, // Always keep data fresh for hybrid cloud environments, but avoid excessive refetching
      queryFn: async (p) => {
        const result = await queryOpts.queryFn(p);

        return result as GetHybridCloudEnvironmentResponseValid;
      },
    };
  },
  listHybridCloudEnvironmentsQuery: (params) =>
    queryOptions(listHybridCloudEnvironments, params, { transport, select: (data) => data.items }),
});

export const useCreateHybridCloudEnvironmentMutation: MutationFactory<typeof createHybridCloudEnvironment> = () =>
  useMutation(createHybridCloudEnvironment, {
    onSuccess: (_data, { hybridCloudEnvironment }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listHybridCloudEnvironments,
            input: { accountId: hybridCloudEnvironment.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId: hybridCloudEnvironment.accountId, cloudProviderId: CLOUD_PROVIDER_MAP.HYBRID },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });

export const useUpdateHybridCloudEnvironmentMutation: MutationFactory<typeof updateHybridCloudEnvironment> = () =>
  useMutation(updateHybridCloudEnvironment, {
    onSuccess: (_data, { hybridCloudEnvironment }, { router: _router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listHybridCloudEnvironments,
            input: { accountId: hybridCloudEnvironment.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getHybridCloudEnvironment,
            input: {
              accountId: hybridCloudEnvironment.accountId,
              hybridCloudEnvironmentId: hybridCloudEnvironment.id,
            },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId: hybridCloudEnvironment.accountId, cloudProviderId: CLOUD_PROVIDER_MAP.HYBRID },
          }),
        }),
      ]),
  });

export const useDeleteHybridCloudEnvironmentMutation: MutationFactory<typeof deleteHybridCloudEnvironment> = () =>
  useMutation(deleteHybridCloudEnvironment, {
    onSuccess: (_data, { hybridCloudEnvironmentId, accountId }, _context, { client }) => {
      client.removeQueries({
        queryKey: createConnectQueryKey({
          schema: getCloudProviderRegion,
          input: {
            accountId,
            cloudProviderId: CLOUD_PROVIDER_MAP.HYBRID,
            regionId: hybridCloudEnvironmentId,
          },
        }),
      });

      return Promise.all([
        // The deleted environment itself, mirroring `useDeleteClusterMutation`.
        // `refetchType: 'none'` only marks it stale: when deleting from the detail page its
        // `HybridCloudEnvironmentPreloader` is still mounted and observing this query, so evicting it
        // (`removeQueries`) makes the next render rebuild an empty query, suspend, re-fetch the deleted
        // environment and throw the 404 into the error boundary, racing the navigation to the list.
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getHybridCloudEnvironment,
            input: { accountId, hybridCloudEnvironmentId },
          }),
          refetchType: 'none',
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listHybridCloudEnvironments,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId, cloudProviderId: CLOUD_PROVIDER_MAP.HYBRID },
          }),
        }),
      ]);
    },
  });

export const useGenerateBootstrapCommandsMutation: MutationFactory<typeof generateBootstrapCommands> = () =>
  useMutation(generateBootstrapCommands, {
    onSuccess: (_data, { hybridCloudEnvironmentId, accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getHybridCloudEnvironment,
            input: { accountId, hybridCloudEnvironmentId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listHybridCloudEnvironments,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCloudProviderRegion,
            input: {
              accountId,
              cloudProviderId: CLOUD_PROVIDER_MAP.HYBRID,
              regionId: hybridCloudEnvironmentId,
            },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });
